import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import { dialCodes } from 'variables/dial-codes';
import type { DialCodeItem } from '../components/CountrySelectionModal/CountrySelectionModal';

const DEFAULT_PREFIX = '357'; // Cyprus

export const E164_MAX_DIGITS = 15;

const NORTH_AMERICAN_CALLING_CODE = '1';
const NORTH_AMERICAN_DEFAULT_COUNTRY = 'US';

interface ParsedPhoneValue {
  prefix: string;
  number: string;
}

interface ResolvePhoneCountryOptions {
  prefix: string;
  number?: string;
  codes?: DialCodeItem[];
  currentCountry?: string | null;
  defaultCountry?: string | null;
  isUserSelected?: boolean;
  allowInference?: boolean;
}

export const getPrefixForCountry = (countryCode: string | null | undefined, codes: DialCodeItem[]): string => {
  if (!countryCode || typeof countryCode !== 'string') return DEFAULT_PREFIX;
  const match = codes.find((c) => c.countryCode === countryCode.toUpperCase());
  return match?.dialCode || DEFAULT_PREFIX;
};

export const getCountryForPrefix = (prefix: string | null | undefined, codes: DialCodeItem[]): string | null => {
  if (!prefix) return null;
  const match = codes.find((c) => c.dialCode === prefix);
  return match?.countryCode || null;
};

export const dialCodeDigits = (dialCode: string | null | undefined): string =>
  typeof dialCode === 'string' ? dialCode.replace(/\D/g, '') : '';

export const parsePhoneValue = (
  value: string | null | undefined,
  codes: DialCodeItem[],
  defaultPrefix: string = DEFAULT_PREFIX
): ParsedPhoneValue => {
  if (!value || typeof value !== 'string') {
    return { prefix: defaultPrefix, number: '' };
  }
  const digits = value.replace(/\D/g, '');
  if (!digits) {
    return { prefix: defaultPrefix, number: '' };
  }
  const sorted = [...codes].sort((a, b) => dialCodeDigits(b.dialCode).length - dialCodeDigits(a.dialCode).length);

  for (let i = 0; i < sorted.length; i++) {
    const { dialCode } = sorted[i];
    const digitsOnlyDialCode = dialCodeDigits(dialCode);

    if (digits.startsWith(digitsOnlyDialCode)) {
      return {
        prefix: dialCode,
        number: digits.slice(digitsOnlyDialCode.length),
      };
    }
  }
  return { prefix: defaultPrefix, number: digits };
};

export const getPhoneNumberPart = (value: string | null | undefined, codes: DialCodeItem[] = dialCodes): string => {
  const parsed = parsePhoneValue(value, codes, DEFAULT_PREFIX);
  return parsed.number;
};

export const toE164 = (prefix: string | null | undefined, nationalNumber?: string | null): string => {
  const prefixPart = dialCodeDigits(prefix);
  const numberPart = dialCodeDigits(nationalNumber);
  if (!prefixPart) return '';
  return `+${prefixPart}${numberPart}`;
};

export const getMaxNationalDigits = (prefix: string | null | undefined): number => {
  const prefixLength = dialCodeDigits(prefix).length;
  return Math.max(E164_MAX_DIGITS - prefixLength, 0);
};

export const getCountriesForPrefix = (
  prefix: string | null | undefined,
  codes: DialCodeItem[] = dialCodes
): string[] => {
  if (!prefix) return [];
  return codes
    .filter((c) => c.dialCode === prefix && c.countryCode)
    .map((c) => c.countryCode as string);
};

export const inferCountryFromE164 = (e164: string | null | undefined): string | null => {
  if (!e164 || typeof e164 !== 'string' || !e164.startsWith('+')) return null;
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed || !parsed.isValid()) return null;
  return parsed.country || null;
};

export const resolvePhoneCountry = ({
  prefix,
  number = '',
  codes = dialCodes,
  currentCountry = null,
  defaultCountry = null,
  isUserSelected = false,
  allowInference = false,
}: ResolvePhoneCountryOptions): string | null => {
  const candidates = getCountriesForPrefix(prefix, codes);
  const held = currentCountry ? currentCountry.toUpperCase() : null;

  if (isUserSelected && held && candidates.includes(held)) return held;

  if (!candidates.length) {
    return allowInference ? inferCountryFromE164(toE164(prefix, number)) : held;
  }

  if (candidates.length === 1) return candidates[0];

  if (allowInference) {
    const inferred = inferCountryFromE164(toE164(prefix, number));
    if (inferred && candidates.includes(inferred)) return inferred;
  }

  if (held && candidates.includes(held)) return held;

  const preferred = defaultCountry ? defaultCountry.toUpperCase() : null;
  if (preferred && candidates.includes(preferred)) return preferred;

  if (dialCodeDigits(prefix) === NORTH_AMERICAN_CALLING_CODE) {
    return NORTH_AMERICAN_DEFAULT_COUNTRY;
  }

  return candidates[0];
};
